import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";
import "./LoginPage.css";

const LoginPage = () => {
  const { user, isLoading, login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("email"); // "email" | "otp"
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (isLoading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);
    try {
      await apiService.requestOtp(email);
      setStep("otp");
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);
    try {
      const authResponse = await apiService.verifyOtp(email, code);
      login(authResponse);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setCode("");
    setIsSubmitting(true);
    try {
      await apiService.requestOtp(email);
    } catch (err) {
      setError(err.message || "Failed to resend code.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="logo-container">
              <img
                src="/itsmybot_logo.webp"
                alt="ItsMyBot Logo"
                className="company-logo"
              />
            </div>
            <h1>Payroll Management</h1>
            {step === "email" && <p>Enter your email to receive a sign-in code</p>}
          </div>

          {error && <div className="error-alert">{error}</div>}

          {step === "email" ? (
            <form onSubmit={handleRequestOtp} className="login-form">
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  className="form-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@itsmybot.com"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary login-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner" style={{ marginRight: "8px" }}></span>
                    Sending code…
                  </>
                ) : (
                  "Send sign-in code"
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="login-form">
              <p style={{ color: "#555", fontSize: "14px", marginBottom: "20px" }}>
                We sent a 6-digit code to <strong>{email}</strong>. It expires in 10 minutes.
              </p>
              <div className="form-group">
                <label htmlFor="code" className="form-label">
                  Sign-in Code
                </label>
                <input
                  type="text"
                  id="code"
                  className="form-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  disabled={isSubmitting}
                  style={{ letterSpacing: "0.2em", fontSize: "1.2rem" }}
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary login-button"
                disabled={isSubmitting || code.length !== 6}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner" style={{ marginRight: "8px" }}></span>
                    Verifying…
                  </>
                ) : (
                  "Verify code"
                )}
              </button>
              <div style={{ display: "flex", gap: "0.5rem", marginTop: "16px", flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={isSubmitting}
                  style={{
                    flex: 1,
                    padding: "0.5rem 0.75rem",
                    fontSize: "0.8rem",
                    fontWeight: "500",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.375rem",
                    background: "white",
                    color: "#374151",
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                    opacity: isSubmitting ? 0.6 : 1,
                    fontFamily: "Figtree, sans-serif",
                  }}
                >
                  Resend code
                </button>
                <button
                  type="button"
                  onClick={() => { setStep("email"); setCode(""); setError(""); }}
                  disabled={isSubmitting}
                  style={{
                    flex: 1,
                    padding: "0.5rem 0.75rem",
                    fontSize: "0.8rem",
                    fontWeight: "500",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.375rem",
                    background: "white",
                    color: "#374151",
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                    opacity: isSubmitting ? 0.6 : 1,
                    fontFamily: "Figtree, sans-serif",
                  }}
                >
                  Use a different email
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
